import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { ControlType } from '@/types/floorPlanViewer';
import { logger } from '@/lib/logger';

export const controlServiceAdapter = {
  async getByFloorPlan(floorPlanId: string): Promise<ControlType[]> {
    const supabase = await getAuthenticatedSupabaseClient();
    const { data, error } = await supabase
      .from('lighting_controls')
      .select('*')
      .eq('floor_plan_id', floorPlanId)
      .order('sequence_number', { ascending: true });

    if (error) throw error;

    // Map database fields to ControlType interface
    return (data || []).map(control => ({
      ...control,
      x: control.x_position,
      y: control.y_position,
    })) as any;
  },

  async create(item: Partial<ControlType>): Promise<ControlType> {
    // Check for default scale if not provided or if using default 1.0
    let finalScale = item.scale;
    if (!finalScale || finalScale === 1.0) {
      try {
        // Import here to avoid circular dependencies
        const elementSizeDefaults = await import('@/services/elementSizeDefaultsService');

        const controlType = item.type;

        if (controlType) {
          // Try to get default scale for this control type
          const defaultScale = await elementSizeDefaults.getDefaultScale(
            'control',
            controlType,
            undefined, // Controls don't have subcategories
          );

          if (defaultScale) {
            finalScale = defaultScale;
          }
        }
      } catch (error) {
        // If fetching defaults fails, just log and continue without defaults
        logger.warn('[controlServiceAdapter] Failed to fetch default scale:', error);
      }
    }

    const supabase = await getAuthenticatedSupabaseClient();
    // Map camelCase to snake_case for database
    const dbItem: any = { ...item, scale: finalScale };

    // Handle all field mappings (both old format and new format)
    if ('x' in item) {
      dbItem.x_position = item.x;
      delete dbItem.x;
    }
    if ('y' in item) {
      dbItem.y_position = item.y;
      delete dbItem.y;
    }
    if ('xPosition' in item) {
      dbItem.x_position = (item as any).xPosition;
      delete dbItem.xPosition;
    }
    if ('yPosition' in item) {
      dbItem.y_position = (item as any).yPosition;
      delete dbItem.yPosition;
    }
    if ('floorPlanId' in item) {
      dbItem.floor_plan_id = (item as any).floorPlanId;
      delete dbItem.floorPlanId;
    }
    if ('roomId' in item) {
      dbItem.room_id = (item as any).roomId;
      delete dbItem.roomId;
    }
    if ('numberOfButtons' in item) {
      dbItem.number_of_buttons = (item as any).numberOfButtons;
      delete dbItem.numberOfButtons;
    }
    if ('productId' in item) {
      dbItem.product_id = (item as any).productId;
      delete dbItem.productId;
    }

    const { data, error } = await supabase
      .from('lighting_controls')
      .insert(dbItem)
      .select()
      .single();

    if (error) throw error;

    // Map back to ControlType format
    return {
      ...data,
      x: data.x_position,
      y: data.y_position,
    } as any;
  },

  async update(id: string, updates: Partial<ControlType>): Promise<ControlType> {
    const supabase = await getAuthenticatedSupabaseClient();
    // Map camelCase to snake_case for database
    const dbUpdates: any = { ...updates };

    // Handle all field mappings (both old format and new format)
    if ('x' in updates) {
      dbUpdates.x_position = updates.x;
      delete dbUpdates.x;
    }
    if ('y' in updates) {
      dbUpdates.y_position = updates.y;
      delete dbUpdates.y;
    }
    if ('xPosition' in updates) {
      dbUpdates.x_position = (updates as any).xPosition;
      delete dbUpdates.xPosition;
    }
    if ('yPosition' in updates) {
      dbUpdates.y_position = (updates as any).yPosition;
      delete dbUpdates.yPosition;
    }
    if ('floorPlanId' in updates) {
      dbUpdates.floor_plan_id = (updates as any).floorPlanId;
      delete dbUpdates.floorPlanId;
    }
    if ('roomId' in updates) {
      dbUpdates.room_id = (updates as any).roomId;
      delete dbUpdates.roomId;
    }
    if ('numberOfButtons' in updates) {
      dbUpdates.number_of_buttons = (updates as any).numberOfButtons;
      delete dbUpdates.numberOfButtons;
    }
    if ('productId' in updates) {
      dbUpdates.product_id = (updates as any).productId;
      delete dbUpdates.productId;
    }

    const { data, error } = await supabase
      .from('lighting_controls')
      .update(dbUpdates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Map back to ControlType format
    return {
      ...data,
      x: data.x_position,
      y: data.y_position,
    } as any;
  },

  async delete(id: string): Promise<boolean> {
    const supabase = await getAuthenticatedSupabaseClient();
    const { error } = await supabase.from('lighting_controls').delete().eq('id', id);

    if (error) throw error;
    return true;
  },
};
